import { Component, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { HelperService } from '../../services/helper.service';
import { BreezeService } from '../../services/breeze.service';
import { NotificationService } from '../../services/notification.service';
import { StorageService } from '../../services/storage.service';

@Component({
    selector: 'app-nin-verification-modal',
    templateUrl: './nin-verification-modal.component.html',
    styleUrls: ['./nin-verification-modal.component.scss'],
    imports: [CommonModule, ReactiveFormsModule]
})
export class NinVerificationModalComponent implements OnInit {
  form: FormGroup = new FormGroup({});
  otpForm: FormGroup = new FormGroup({});
  step: 'details' | 'otp' = 'details';
  isSubmitting = false;
  isResending = false;
  private phonePattern = /^0[789][01]\d{8}$/;

  constructor(
    public modal: NgbActiveModal,
    private notify: NotificationService,
    private fb: FormBuilder,
    private helperService: HelperService,
    private breezeService: BreezeService,
    private storage: StorageService,
  ) {}

  ngOnInit(): void {
    const profile = this.storage.get('user');
    this.form = this.fb.group({
      nin: ['', [Validators.required, Validators.pattern(/^\d{11}$/)]],
      phoneNumber: [
        { value: profile?.mobileNumber ?? '', disabled: true },
        [Validators.required, Validators.pattern(this.phonePattern)],
      ],
    });
    this.otpForm = this.fb.group({
      otp: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]],
    });
  }

  showError(formControlName: string) {
    return (
      (this.form.get(formControlName)?.dirty ||
        this.form.get(formControlName)?.touched) &&
      this.form.get(formControlName)?.invalid
    );
  }

  showOtpError() {
    const control = this.otpForm.get('otp');
    return (control?.dirty || control?.touched) && control?.invalid;
  }

  onVerify() {
    if (this.form.invalid) {
      this.helperService.validateAllFormFields(this.form);
      return;
    }

    const profile = this.storage.get('user');
    if (!profile?.emailAddress) {
      this.notify.error(
        'Verification Failed',
        'We could not read your profile details. Please sign in again.',
      );
      return;
    }

    this.isSubmitting = true;
    const payload = {
      nin: String(this.form.get('nin')?.value ?? ''),
      phone_number: this.form.get('phoneNumber')?.value,
      first_name: profile?.name ?? '',
      last_name: profile?.family ?? '',
      email: profile?.emailAddress ?? '',
    };

    this.breezeService.verifyNinWithPhone(payload).subscribe({
      // this.breezeService.verifyNINV2(payload, 'R').subscribe({
      next: (response: any) => {
        this.isSubmitting = false;
        if (!response?.success) {
          this.notify.error(
            'Verification Failed',
            response?.message ||
              'Unable to verify your NIN with the phone number provided. Please check your details and try again.',
          );
          return;
        }
        this.notify.success(
          response.message || 'NIN verified. OTP sent to your phone number.',
        );
        this.step = 'otp';
      },
      error: (error) => {
        this.isSubmitting = false;
        const message = this.breezeService.extractBackendMessage(
          error?.error ?? error,
        );
        this.notify.error(
          'Verification Failed',
          message ||
            'Unable to verify your NIN with the phone number provided. Please check your details and try again.',
        );
      },
    });
  }

  onVerifyOtp() {
    if (this.otpForm.invalid) {
      this.helperService.validateAllFormFields(this.otpForm);
      return;
    }

    this.isSubmitting = true;
    const phoneNumber = this.form.get('phoneNumber')?.value;
    const otp = this.otpForm.get('otp')?.value;

    this.breezeService.verifyNinOtp(phoneNumber, otp).subscribe({
      next: (response) => {
        this.isSubmitting = false;
        if (!response?.success) {
          this.notify.error(
            'OTP Verification Failed',
            response?.message || 'Invalid or expired OTP',
          );
          return;
        }
        this.notify.success(response.message || 'OTP verified successfully');
        this.modal.close('verified');
      },
      error: (error) => {
        this.isSubmitting = false;
        const message = this.breezeService.extractBackendMessage(
          error?.error ?? error,
        );
        this.notify.error(
          'OTP Verification Failed',
          message || 'Invalid or expired OTP',
        );
      },
    });
  }

  onResendOtp() {
    if (this.isResending || this.isSubmitting) {
      return;
    }
    this.isResending = true;
    const payload = {
      nin: String(this.form.get('nin')?.value ?? ''),
      phone_number: this.form.get('phoneNumber')?.value,
    };
    this.breezeService.verifyNinWithPhone(payload).subscribe({
      next: (response) => {
        this.isResending = false;
        if (!response?.success) {
          this.notify.error(
            'Resend Failed',
            response?.message || 'Unable to resend OTP. Please try again.',
          );
          return;
        }
        this.notify.success('OTP sent to your phone number.');
      },
      error: (error) => {
        this.isResending = false;
        const message = this.breezeService.extractBackendMessage(
          error?.error ?? error,
        );
        this.notify.error(
          'Resend Failed',
          message || 'Unable to resend OTP. Please try again.',
        );
      },
    });
  }

  onBackToDetails() {
    if (this.isSubmitting) {
      return;
    }
    this.otpForm.reset();
    this.step = 'details';
  }
}
